import { get } from 'lodash-es';
import { getWithWildcard } from '../../utils/property-path';
import type {
  ItemMapper
} from '../types/item-mapper.interface';
import {ItemMapperConfig} from "../types/item-mapper-config.interface";
import {MatrixEntry} from "../types/matrix-item.interface";
import {AxisRangeConfig} from "../types/axis-range-config.type";

export class ConfigurableItemMapper<TXValue, TYValue, TItem> implements ItemMapper<TXValue, TYValue, TItem> {
  /**
   * Global insert order counter (static across all instances)
   *
   * This ensures consistent Z-Index ordering across different providers:
   * - Provider A adds item 1 → insertOrder: 0
   * - Provider B adds item 2 → insertOrder: 1 (NOT 0 again!)
   * - Provider A adds item 3 → insertOrder: 2
   *
   * Result: Item 3 renders on top of item 2, which renders on top of item 1.
   */
  private static insertOrderCounter = 0;

  constructor(private config: ItemMapperConfig<TItem>) {}

  /**
   * Map a domain item to one or more SpatialMatrix entries
   *
   * Algorithm:
   * 1. Extract TXAxisValue-range (always [start, end])
   * 2. Extract TYAxisValue-values (can be single value or array for multi-assignment)
   * 3. For each TYAxisValue-value, create a MatrixEntry
   * 4. All entries share the SAME data reference (memory optimization)
   *
   * @param item - Domain item to map
   * @returns Array of MatrixEntry (empty if no TYAxisValue-values, 1+ otherwise)
   */
  mapToMatrixEntries(item: TItem): Array<MatrixEntry<TXValue, TYValue, TItem>> {

    // Step 1: Extract TXAxisValue-range
    const xRange = this.extractRange(item, this.config.x) as [TXValue, TXValue];

    // Step 2: Extract TYAxisValue-values (single or multiple)
    const yValues = this.extractValues(item, this.config.y) as TYValue[];

    // Step 3: Map each TYAxisValue-value to a MatrixEntry
    return yValues.map(yValue => ({
      id: this.generateId(item, yValue),
      spanX: xRange,
      indexRangeY: [yValue, yValue] as [TYValue, TYValue],  // Always [value, value] for single row
      data: item,  // SAME reference for all entries
      insertOrder: ConfigurableItemMapper.insertOrderCounter++
    }));
  }

  /**
   * Extract range [start, end] from item using AxisRangeConfig
   *
   * Handles 3 modes:
   * - Function: Call function, convert result to [start, end]
   * - String: Extract value via lodash.get(), convert to Point [value, value]
   * - Range Object: Extract start/end separately via lodash.get()
   *
   * @param item - Domain item
   * @param config - Axis configuration
   * @returns [start, end] range
   *
   * @private
   */
  private extractRange<T>(item: TItem, config: AxisRangeConfig<TItem>): [T, T] {
    if (typeof config === 'function') {
      // Function fallback
      const result = config(item);
      // Result can be [start, end] or single value → convert to Point
      return Array.isArray(result) ? result as [T, T] : [result, result];
    } else if (typeof config === 'string') {
      // Single value → Point [value, value]
      const value = get(item, config) as T;
      return [value, value];
    } else {
      // Range → [start, end]
      const start = get(item, config.start) as T;
      const end = get(item, config.end) as T;
      return [start, end];
    }
  }

  /**
   * Extract TYAxisValue-values from item using AxisRangeConfig
   *
   * Handles 3 modes:
   * - Function: Call function, ensure result is array
   * - String: Extract value via getWithWildcard(), ensure result is array
   * - Range Object: Extract start value, wrap in array
   *
   * For multi-assignment:
   * - If extracted value is array → return as-is
   * - If extracted value is single value → wrap in array [value]
   *
   * Wildcard support (getWithWildcard):
   * - "assignees[*].uid" → automatically extracts array of UIDs
   * - "typeUid" → extracts single value, wrapped in array
   * - Nested wildcards supported: "teams[*].members[*].id" → flattened array
   *
   * @param item - Domain item
   * @param config - Axis configuration
   * @returns Array of TYAxisValue-values (empty for empty arrays, 1+ otherwise)
   *
   * @private
   */
  private extractValues<T>(item: TItem, config: AxisRangeConfig<TItem>): T[] {
    if (typeof config === 'function') {
      // Function fallback
      const result = config(item);
      return Array.isArray(result) ? result : [result];
    } else if (typeof config === 'string') {
      // Property path with wildcard support (may return array for multi-assignment)
      const result = getWithWildcard(item, config);

      // Handle undefined/null (e.g., missing property)
      if (result === undefined || result === null) {
        return [];
      }

      return Array.isArray(result) ? result : [result];
    } else {
      // Range: extract start value for TYAxisValue-axis
      const [start] = this.extractRange(item, config) as [T, T];
      return [start];
    }
  }

  /**
   * Generate unique ID for matrix entry
   *
   * Format:
   * - Single-assignment: Uses item ID directly (e.g., "holiday-1")
   * - Multi-assignment: Composite ID (e.g., "task-123_alice", "task-123_bob")
   *
   * Fallback:
   * - If idPath not specified → try "id" or "uid" properties
   * - If yValue is string → append to item ID
   * - If yValue is not string → use item ID only (no suffix)
   *
   * @param item - Domain item
   * @param yValue - TYAxisValue-axis value (for composite ID)
   * @returns Unique ID string
   *
   * @private
   */
  private generateId(item: TItem, yValue: TYValue): string {
    // Extract item ID via idPath (or fallback to "id"/"uid")
    let itemId: string;
    if (this.config.idPath) {
      itemId = get(item, this.config.idPath) as string;
    } else {
      // Fallback: Try "id" or "uid"
      itemId = (item as any).id || (item as any).uid;
    }

    // Composite ID for multi-assignment
    // Only append yValue if it's a string (typical case for resource UUIDs)
    if (typeof yValue === 'string') {
      return `${itemId}_${yValue}`;
    } else {
      // Edge-case: Non-string TYAxisValue-values (rare)
      // Use item ID only (may cause duplicate IDs if multi-assignment with non-string TYAxisValue)
      return itemId;
    }
  }
}
